import { formatEducationSystemLevel } from '@tutor/types';
import type { MusicCaseDetailsData, ServiceType, TutorData } from '@/types/api';

type CaseDisplayLike = {
  educationSystem?: string | null;
  level?: string | null;
  subject?: string | null;
  serviceType?: ServiceType | null;
  musicDetails?: MusicCaseDetailsData | null;
};

export const getCaseServiceType = (
  caseData?: Pick<CaseDisplayLike, 'serviceType'> | null
) => caseData?.serviceType ?? 'academic';

export const formatCaseTitle = (caseData: CaseDisplayLike): string => {
  if (getCaseServiceType(caseData) === 'music') {
    const details = caseData.musicDetails;
    const parts = [
      details?.instrument,
      details?.learningGoal || details?.musicLevel,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' - ') : 'Music tutoring request';
  }

  return `${formatEducationSystemLevel(caseData.educationSystem ?? 'national_school', caseData.level ?? '', {
    useShortSystemLabel: true,
  })} - ${caseData.subject}`;
};

export const formatCaseSummary = (caseData: CaseDisplayLike): string => {
  if (getCaseServiceType(caseData) === 'music') {
    const details = caseData.musicDetails;
    return [
      details?.instrument,
      details?.musicLevel,
      details?.lessonFormat,
    ].filter(Boolean).join(' · ') || 'Music tutoring';
  }

  return formatCaseTitle(caseData);
};

export const formatTutorTeachingSummary = (tutor: TutorData): string => {
  const verticals = tutor.serviceTypes?.length ? tutor.serviceTypes : ['academic'];
  const parts: string[] = [];

  if (verticals.includes('academic')) {
    const subjects = Array.from(new Set(
      tutor.subjectCards?.flatMap((card) => card.subjects ?? []) ?? []
    ));
    if (subjects.length > 0) parts.push(`Academic: ${subjects.slice(0, 4).join(', ')}`);
  }

  if (verticals.includes('music')) {
    const instruments = tutor.musicProfile?.instruments ?? [];
    parts.push(instruments.length > 0 ? `Music: ${instruments.slice(0, 4).join(', ')}` : 'Music');
  }

  return parts.join(' | ') || 'Teaching profile';
};
